if (!customElements.get("dock-search")) {
  var DOCK_DESKTOP = window.matchMedia("(min-width: 880px)");

  class DockSearch extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector("[data-dock-panel]");
      this.form = this.querySelector("[data-dock-form]");
      this.input = this.querySelector("[data-dock-input]");
      this.list = this.querySelector("[data-dock-list]");
      this.results = this.querySelector("[data-dock-results]");
      this.empty = this.querySelector("[data-dock-empty]");
      this.noresults = this.querySelector("[data-dock-noresults]");
      this.notice = this.querySelector("[data-dock-notice]");
      this.preview = this.querySelector("[data-dock-preview-pane]");
      this.all = this.querySelector("[data-dock-all]");
      this.replacements = Array.from(this.querySelectorAll("template[data-dock-replacement]")).map((t) => ({
        term: t.getAttribute("data-term") || "",
        query: t.getAttribute("data-query") || "",
        template: t,
      }));

      this.suggestUrl = this.getAttribute("data-suggest-url");
      this.searchUrl = this.getAttribute("data-search-url");
      this.active = -1;
      this.timer = null;
      this.controller = null;
      this.cache = {};
      this.querySource = "typed";

      // Closed: out of the tab order and the accessibility tree
      this.panel.inert = true;

      this.bindTriggers();
      this.bindPanel();
    }

    // ----- Opening / closing -----

    bindTriggers() {
      // Header icon and tab bar both click wdn-drawer-open[data-id="search"]: take over that click
      document.addEventListener(
        "click",
        (e) => {
          // The tab bar opens search by clicking the header trigger: remember where it started
          if (e.target.closest && e.target.closest("[data-tab-action='search']")) this.openedFrom = "tab_bar";
          var trigger = e.target.closest && e.target.closest("wdn-drawer-open[data-id='search'], [data-dock-open]");
          if (!trigger) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          this.open(trigger);
        },
        true
      );

      document.addEventListener("keydown", (e) => {
        var typing = e.target.closest && e.target.closest("input, textarea, select, [contenteditable]");
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
          e.preventDefault();
          this.isOpen ? this.close() : this.open();
        } else if (e.key === "/" && !typing && !this.isOpen) {
          e.preventDefault();
          this.open();
        } else if (e.key === "Escape" && this.isOpen) {
          this.close();
        }
      });
    }

    open(trigger) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.returnFocus = trigger || document.activeElement;

      // Close the menu / cart drawer if one is open (the tab row in the menu opens search too)
      document.querySelectorAll("wdn-drawer").forEach((drawer) => {
        if (drawer.isActive && drawer.close) drawer.close();
      });

      this.measureHeader();
      this.panel.inert = false;
      this.setAttribute("open", "");
      document.body.classList.add("dock-open");
      this.lockScroll();

      // Desktop: ready to type. Mobile: open fully expanded, the keyboard
      // only comes up once the field is tapped
      if (DOCK_DESKTOP.matches) this.input.focus({ preventScroll: true });
      this.watchKeyboard(true);
      if (DOCK_DESKTOP.matches) this.setActive(0);

      this.session = { searches: 0, clicks: 0 };
      var from = this.openedFrom || (!trigger ? "shortcut" : trigger.closest && trigger.closest("[data-header]") ? "header" : "other");
      this.openedFrom = null;
      this.track("open", { trigger: from });
    }

    close() {
      if (!this.isOpen) return;
      this.flushQuery();
      this.track("close", {
        search_term: this.input.value.trim(),
        searches: this.session ? this.session.searches : 0,
        clicks: this.session ? this.session.clicks : 0,
      });
      this.isOpen = false;
      this.panel.inert = true;
      this.removeAttribute("open");
      this.removeAttribute("data-kb");
      document.body.classList.remove("dock-open");
      this.unlockScroll();
      this.input.blur();
      this.watchKeyboard(false);

      var target = this.returnFocus;
      // The header trigger is an empty element: focus its icon wrapper instead
      if (target && target.matches && target.matches("wdn-drawer-open")) target = null;
      if (target && target.focus && DOCK_DESKTOP.matches) target.focus({ preventScroll: true });
    }

    // overflow: hidden alone doesn't stop iOS Safari from scrolling the page behind,
    // so the body is pinned in place and put back where it was on close
    lockScroll() {
      document.documentElement.classList.add("dock-locked");
      // Desktop: overflow: hidden is enough, and pinning would make the scrollbar jump
      this.pinned = !DOCK_DESKTOP.matches;
      if (!this.pinned) return;
      this.lockedY = window.scrollY;
      var body = document.body.style;
      body.position = "fixed";
      body.top = -this.lockedY + "px";
      body.left = "0";
      body.right = "0";
      body.width = "100%";
    }

    unlockScroll() {
      document.documentElement.classList.remove("dock-locked");
      if (!this.pinned) return;
      this.pinned = false;
      var body = document.body.style;
      body.position = "";
      body.top = "";
      body.left = "";
      body.right = "";
      body.width = "";
      window.scrollTo(0, this.lockedY || 0);
    }

    measureHeader() {
      var header = document.querySelector("[data-header]");
      var bottom = header ? Math.max(header.getBoundingClientRect().bottom, 0) : 0;
      this.style.setProperty("--dock-header", bottom + "px");
      this.style.setProperty("--dock-top", bottom + 8 + "px");
    }

    // The sheet's bottom edge follows the on-screen keyboard
    watchKeyboard(on) {
      var vv = window.visualViewport;
      if (!vv) return;
      if (!this.onViewport) {
        this.onViewport = () => {
          var kb = Math.max(window.innerHeight - vv.height - vv.offsetTop, 0);
          this.style.setProperty("--dock-kb", kb + "px");
          // The mobile sheet is sized to the visible part of the screen
          this.style.setProperty("--dock-vv-top", vv.offsetTop + "px");
          this.style.setProperty("--dock-vv-h", vv.height + "px");
          this.toggleAttribute("data-kb", kb > 80);
        };
      }
      if (on) {
        vv.addEventListener("resize", this.onViewport);
        vv.addEventListener("scroll", this.onViewport);
        this.onViewport();
      } else {
        vv.removeEventListener("resize", this.onViewport);
        vv.removeEventListener("scroll", this.onViewport);
        this.style.setProperty("--dock-kb", "0px");
        this.style.removeProperty("--dock-vv-top");
        this.style.removeProperty("--dock-vv-h");
      }
    }

    // ----- Panel events -----

    bindPanel() {
      this.querySelectorAll("[data-dock-close]").forEach((el) => {
        el.addEventListener("click", () => this.close());
      });

      // The X clears the text first, then closes
      this.dismiss = this.querySelector("[data-dock-dismiss]");
      this.dismiss.addEventListener("click", () => {
        if (this.input.value) {
          this.input.value = "";
          this.onInput(true);
          this.input.focus({ preventScroll: true });
        } else {
          this.close();
        }
      });

      this.input.addEventListener("input", () => {
        this.querySource = "typed";
        this.onInput();
      });
      this.input.addEventListener("keydown", (e) => this.onKey(e));

      // Results stay in the widget: no trip to the /search page.
      // Mobile: the search key hides the keyboard so the sheet expands.
      // Desktop: Enter opens the highlighted result (the first one by default).
      this.form.addEventListener("submit", (e) => {
        e.preventDefault();
        var q = this.input.value.trim();
        if (!q) return;
        if (DOCK_DESKTOP.matches) {
          var row = this.items()[this.active];
          var link = row && row.querySelector("[data-dock-link]");
          if (link) link.click();
        } else {
          this.expand();
        }
      });

      // "See all results" (mobile only) also just expands the sheet
      this.all.addEventListener("click", (e) => {
        e.preventDefault();
        this.expand();
      });

      // Only the result list (and the sideways chip / need rows) may scroll on touch;
      // a drag anywhere else in the dock would otherwise reach the page
      this.addEventListener(
        "touchmove",
        (e) => {
          if (!e.target.closest("[data-dock-list]")) e.preventDefault();
        },
        { passive: false }
      );

      // Scrolling the results hides the keyboard on mobile, like the prototype
      this.list.addEventListener(
        "touchmove",
        () => {
          if (!DOCK_DESKTOP.matches && document.activeElement === this.input) this.input.blur();
        },
        { passive: true }
      );

      this.addEventListener("click", (e) => {
        var query = e.target.closest("[data-dock-query]");
        if (query) {
          this.setQuery(query.getAttribute("data-dock-query"), query.closest("[data-dock-empty]") ? "popular" : "suggested");
          return;
        }

        var add = e.target.closest("[data-dock-add]");
        if (add) {
          e.preventDefault();
          this.addToCart(add);
          return;
        }

        // Links navigate on their own (after the click is tracked)
        var clicked = e.target.closest("[data-dock-link]");
        if (clicked) {
          this.trackClick(clicked, e);
          return;
        }

        // Desktop: the whole row is clickable
        var row = e.target.closest("[data-dock-item]");
        if (row && DOCK_DESKTOP.matches) {
          var rowLink = row.querySelector("[data-dock-link]");
          if (rowLink) rowLink.click();
        }
      });

      this.list.addEventListener("mousemove", (e) => {
        if (!DOCK_DESKTOP.matches) return;
        var row = e.target.closest("[data-dock-item]");
        if (!row) return;
        var index = this.items().indexOf(row);
        if (index !== this.active) this.setActive(index, false);
      });

      DOCK_DESKTOP.addEventListener("change", () => {
        if (this.isOpen) this.measureHeader();
        this.setActive(DOCK_DESKTOP.matches ? 0 : -1);
      });
    }

    onKey(e) {
      if (!DOCK_DESKTOP.matches) return;
      var items = this.items();
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        if (!items.length) return;
        var step = e.key === "ArrowDown" ? 1 : -1;
        this.setActive((this.active + step + items.length) % items.length);
      }
    }

    // Mobile: drop the keyboard so the sheet grows to the bottom of the screen;
    // every result is then in view, so the "See all" link goes away
    expand() {
      this.input.blur();
      this.all.hidden = true;
    }

    // ----- Searching -----

    setQuery(q, source) {
      this.querySource = source || "typed";
      this.input.value = q;
      this.onInput(true);
      // On mobile a chip drops the keyboard, so the sheet stays fully expanded
      if (DOCK_DESKTOP.matches) this.input.focus({ preventScroll: true });
      else this.input.blur();
    }

    onInput(now) {
      var q = this.input.value.trim();
      this.toggleAttribute("data-has-query", q.length > 0);
      this.dismiss.setAttribute(
        "aria-label",
        this.dismiss.getAttribute(q.length > 0 ? "data-label-clear" : "data-label-close")
      );
      clearTimeout(this.timer);

      if (!q) {
        if (this.controller) this.controller.abort();
        this.showEmpty();
        return;
      }
      this.timer = setTimeout(() => this.search(q), now ? 0 : 140);
    }

    replacementFor(q) {
      var lower = q.toLowerCase();
      if (lower.length < 3) return null;
      for (var i = 0; i < this.replacements.length; i++) {
        var r = this.replacements[i];
        if (!r.term || !r.query) continue;
        if (r.term.indexOf(lower) === 0 || lower.indexOf(r.term) === 0) return r;
      }
      return null;
    }

    search(q) {
      var replacement = this.replacementFor(q);
      var fetchQ = replacement ? replacement.query : q;

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      var url =
        this.suggestUrl +
        "?q=" + encodeURIComponent(fetchQ) +
        "&resources[type]=product,collection,article,query" +
        "&resources[limit]=10" +
        "&resources[limit_scope]=each" +
        "&resources[options][unavailable_products]=last" +
        "&section_id=dock-search-results";

      var render = (html) => {
        if (this.input.value.trim() !== q) return;
        this.showResults(q, html, replacement);
      };

      if (this.cache[url]) {
        render(this.cache[url]);
        return;
      }

      fetch(url, { signal: this.controller.signal })
        .then((res) => res.text())
        .then((text) => {
          var doc = new DOMParser().parseFromString(text, "text/html");
          var inner = doc.querySelector("[data-dock-results-inner]");
          var html = inner ? inner.outerHTML : "";
          this.cache[url] = html;
          render(html);
        })
        .catch((err) => {
          if (err.name !== "AbortError") console.error("Dock search:", err);
        });
    }

    showEmpty() {
      this.results.hidden = true;
      this.results.innerHTML = "";
      this.noresults.hidden = true;
      this.notice.hidden = true;
      this.notice.innerHTML = "";
      this.empty.hidden = false;
      this.all.hidden = true;
      this.list.scrollTop = 0;
      this.setActive(DOCK_DESKTOP.matches ? 0 : -1);
    }

    showResults(q, html, replacement) {
      this.results.innerHTML = html;
      var countEl = this.results.querySelector("[data-dock-count]");
      var count = countEl ? parseInt(countEl.getAttribute("data-dock-count"), 10) : 0;

      if (replacement) {
        this.notice.innerHTML = "";
        this.notice.appendChild(replacement.template.content.cloneNode(true));
        this.notice.hidden = false;
      } else {
        this.notice.hidden = true;
        this.notice.innerHTML = "";
      }

      if (!replacement) this.highlight(q);

      var none = count === 0;
      this.results.hidden = none;
      this.noresults.hidden = !none;
      this.noresults.querySelector("[data-dock-noresults-q]").textContent = q;
      // No results: show the popular searches and products underneath
      this.empty.hidden = !none;

      this.all.hidden = none;
      this.all.href = this.searchUrl + "?q=" + encodeURIComponent(q) + "&type=product";
      this.all.querySelector("[data-dock-all-q]").textContent = q;

      this.list.scrollTop = 0;
      this.setActive(DOCK_DESKTOP.matches ? 0 : -1);

      this.queueQuery(q, count, replacement);
    }

    // Bold the typed letters in result titles
    highlight(q) {
      var needle = q.toLowerCase();
      this.results.querySelectorAll("[data-dock-title]").forEach((el) => {
        var text = el.textContent;
        var i = text.toLowerCase().indexOf(needle);
        if (i < 0) return;
        el.textContent = "";
        el.append(text.slice(0, i));
        var mark = document.createElement("mark");
        mark.textContent = text.slice(i, i + needle.length);
        el.append(mark, text.slice(i + needle.length));
      });
    }

    // ----- Rows + preview (desktop) -----

    items() {
      var scope = this.results.hidden ? this.empty : this.results;
      return Array.from(scope.querySelectorAll("[data-dock-item]")).filter((el) => el.offsetParent !== null);
    }

    setActive(index, scroll) {
      var items = this.items();
      items.forEach((el, i) => el.classList.toggle("is-active", i === index));
      this.active = index;

      if (!DOCK_DESKTOP.matches || index < 0 || !items[index]) {
        this.preview.innerHTML = "";
        return;
      }
      var row = items[index];
      if (scroll !== false) row.scrollIntoView({ block: "nearest" });
      var template = row.querySelector("template[data-dock-preview]");
      this.preview.innerHTML = "";
      if (template) {
        this.preview.appendChild(template.content.cloneNode(true));
        this.syncAdded(this.preview);
      }
    }

    // ----- Cart -----

    addToCart(button) {
      var id = button.getAttribute("data-dock-add");
      if (!id || button.hasAttribute("data-loading")) return;
      if (button.hasAttribute("data-added")) {
        window.location.href = window.Shopify.routes.root + "cart";
        return;
      }
      button.setAttribute("data-loading", "");
      this.flushQuery();
      var title = button.closest(".dock-preview");
      title = title && title.querySelector(".dock-preview__title");
      this.track("add_to_cart", {
        search_term: this.input.value.trim(),
        item_title: title ? title.textContent.trim() : "",
        variant_id: id,
      });

      fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      })
        .then((res) => {
          if (!res.ok) throw new Error("Add to cart failed");
          return res.json();
        })
        .then((json) => {
          this.added = this.added || {};
          this.added[id] = true;
          this.syncAdded(this);
          document.body.dispatchEvent(new CustomEvent("cart:updated", { detail: json }));
          if (window.WDN && WDN.CartDrawer && WDN.CartDrawer.refresh) {
            WDN.CartDrawer.refresh({ openDrawer: false });
          } else if (window.WDN && WDN.CartCount) {
            WDN.CartCount.update();
          }
        })
        .catch((err) => console.error("Dock search:", err))
        .finally(() => {
          this.querySelectorAll("[data-dock-add='" + id + "']").forEach((b) => b.removeAttribute("data-loading"));
        });
    }

    syncAdded(scope) {
      if (!this.added) return;
      scope.querySelectorAll("[data-dock-add]").forEach((b) => {
        b.toggleAttribute("data-added", !!this.added[b.getAttribute("data-dock-add")]);
      });
    }

    // ----- Tracking -----
    // Each action is a Shopify customer event ("search_widget_*"); a custom pixel
    // (Settings > Customer events) forwards them to GA4. Consent is handled by Shopify.

    track(name, data) {
      try {
        if (!window.Shopify || !Shopify.analytics || !Shopify.analytics.publish) return;
        data = data || {};
        data.device = DOCK_DESKTOP.matches ? "desktop" : "mobile";
        Shopify.analytics.publish("search_widget_" + name, data);
      } catch (err) {}
    }

    // A search counts once the shopper stops typing (1s), or straight away for a chip
    queueQuery(q, count, replacement) {
      clearTimeout(this.queryTimer);
      this.pendingQuery = {
        search_term: q,
        results_count: count,
        source: this.querySource,
        replaced_with: replacement ? replacement.query : "",
      };
      this.queryTimer = setTimeout(() => this.flushQuery(), this.querySource === "typed" ? 1000 : 0);
    }

    flushQuery() {
      clearTimeout(this.queryTimer);
      var pending = this.pendingQuery;
      this.pendingQuery = null;
      if (!pending) return;
      var key = pending.search_term.toLowerCase();
      if (key === this.lastTracked) return;
      this.lastTracked = key;
      if (this.session) this.session.searches++;
      this.track("query", pending);
      if (pending.results_count === 0) this.track("no_results", { search_term: pending.search_term });
    }

    trackClick(link, e) {
      this.flushQuery();
      if (this.session) this.session.clicks++;

      var href = link.getAttribute("href") || "";
      var inPreview = this.preview.contains(link);
      // A click in the desktop preview belongs to the highlighted row
      var row = inPreview ? this.items()[this.active] : link.closest("[data-dock-item]");
      var type = href.indexOf("/products/") > -1 ? "product" : href.indexOf("/collections/") > -1 ? "collection" : href.indexOf("/blogs/") > -1 ? "article" : "other";
      var list = "results";
      if (row && row.classList.contains("dock-need")) list = "shop_by_need";
      else if (row && this.empty.contains(row)) list = "popular_products";
      if (link.classList.contains("dock-row__outlet")) list = "outlet";

      var title = row && row.querySelector("[data-dock-title], .dock-need__title");
      this.track("click", {
        search_term: this.input.value.trim(),
        item_type: type,
        item_title: (title ? title.textContent : link.textContent).trim().replace(/\s+/g, " ").slice(0, 100),
        item_url: href.split("?")[0],
        item_list: list,
        position: row && row.parentElement ? Array.from(row.parentElement.querySelectorAll(":scope > [data-dock-item]")).indexOf(row) + 1 : 0,
        from_preview: inPreview,
      });

      // Give the event a moment to leave before the page changes (plain left clicks only)
      if (e && !e.defaultPrevented && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && link.target !== "_blank" && href) {
        e.preventDefault();
        setTimeout(() => (window.location.href = href), 150);
      }
    }
  }

  customElements.define("dock-search", DockSearch);
}
